import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PermissionsContext = createContext({
  permissions: {},
  modules: {},
  pathMap: {},
  role: null,
  user: null,
  loading: true,
  hasPermission: () => false,
  isModuleEnabled: () => false,
  moduleAction: () => false,
  refreshPermissions: () => {},
});

export const PermissionsProvider = ({ children }) => {
  const [permissions, setPermissions] = useState({});
  const [modules, setModules] = useState({});
  const [pathMap, setPathMap] = useState({});
  const [role, setRole] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/permissions`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setPermissions(data.permissions || {});
        setModules(data.modules || {});
        setPathMap(data.path_map || {});
        setRole(data.role);
        setUser(data.user || null);
      }
    } catch (error) {
      logger.error('Error fetching permissions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const hasPermission = (permission) => {
    return permissions[permission] === true;
  };

  // Check if a module is enabled for the current user
  const isModuleEnabled = (moduleId) => {
    const mod = modules[moduleId];
    if (!mod) return true; // Default to enabled if not configured
    return mod.enabled !== false;
  };

  // Check a specific action (view/edit/delete) for a module
  const moduleAction = (moduleId, action) => {
    const mod = modules[moduleId];
    if (!mod) return true;
    if (!mod.enabled) return false;
    return mod[action] !== false;
  };

  return (
    <PermissionsContext.Provider value={{
      permissions, modules, pathMap, role, user, loading,
      hasPermission, isModuleEnabled, moduleAction,
      refreshPermissions: fetchPermissions,
    }}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  return useContext(PermissionsContext);
};

export default PermissionsContext;
